/**
 * RST button for the trading bars (desktop order bar, mobile trading bar).
 * It controls the FUTURES Restrain venue (perps / Vest has its own, set in
 * Discipline settings, the widget and the extension). The label shows the
 * setting ("RST" off, "RST 30m" / "RST 2h" / "RST flat" on); a click opens a
 * small preset menu. Choosing a preset saves restrainFuturesEnabled=true +
 * restrainFuturesMinutes in ONE discipline settings update; Off saves
 * restrainFuturesEnabled=false and keeps the minutes.
 *
 * While a futures restrain is running the server refuses turning it off or
 * changing the duration (RESTRAIN_LOCKED), so the menu shows the lock and
 * disables every choice. A running perps restrain does not lock it. Armed
 * with no position yet, everything can be changed. It is one-shot: the server
 * switches futures off when the last restrained futures position is flat.
 *
 * The menu is portalled to body (the bars clip overflow) and positioned with
 * clampPopover: under the button when it fits, above it otherwise (the mobile
 * bar sits at the bottom of the screen), always inside the viewport.
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDisciplineStore } from '../../store/disciplineStore';
import {
  RESTRAIN_PRESETS,
  RESTRAIN_VENUE_KEYS,
  formatRestrainMinutes,
  restrainButtonLabel,
  restrainLockLabel,
  type RestrainVenue,
} from '../../store/restrainStore';
import { useRestrainLock } from './useRestrain';
import './Restrain.css';

export const RESTRAIN_EXPLANATION =
  'While in a position, size is frozen: no adding, no partial exits. You can still flatten and move TP/SL. Resets when flat.';

export const RESTRAIN_MENU_FOOTER_LOCKED =
  "Running - can't be turned off or changed until the timer ends or the position is flat. You can still exit or move TP/SL.";
export const RESTRAIN_MENU_FOOTER_ARMED = 'Armed for next position. Turns off by itself when that position is flat.';
export const RESTRAIN_MENU_FOOTER_OFF = 'Pick a duration to arm it for the next position.';

export interface Rect { top: number; bottom: number; left: number }
export interface Size { width: number; height: number }

const GAP = 4;
const MARGIN = 8;

/**
 * Where to put a popover of `size` for a trigger at `anchor`: below it when it
 * fits, else above it when that fits, else whichever side has more room; then
 * clamped so it stays MARGIN px inside the viewport on every edge.
 */
export function clampPopover(anchor: Rect, size: Size, viewport: Size): { top: number; left: number } {
  const below = anchor.bottom + GAP;
  const above = anchor.top - GAP - size.height;
  const fitsBelow = below + size.height <= viewport.height - MARGIN;
  const fitsAbove = above >= MARGIN;
  let top: number;
  if (fitsBelow) top = below;
  else if (fitsAbove) top = above;
  else top = viewport.height - anchor.bottom > anchor.top ? below : above;
  top = Math.max(MARGIN, Math.min(top, viewport.height - size.height - MARGIN));
  const left = Math.max(MARGIN, Math.min(anchor.left, viewport.width - size.width - MARGIN));
  return { top, left };
}

/** First guess before the menu has rendered and can be measured. */
const ESTIMATED_SIZE: Size = { width: 216, height: 160 };

interface RestrainToggleProps {
  className?: string;
  /** Which venue it controls. The trading bars' RST is futures. */
  venue?: RestrainVenue;
}

const RestrainToggle: React.FC<RestrainToggleProps> = ({ className, venue = 'futures' }) => {
  const keys = RESTRAIN_VENUE_KEYS[venue];
  const enabled = useDisciplineStore(s => s.settings[keys.enabled]);
  const minutes = useDisciplineStore(s => s.settings[keys.minutes]);
  const updateSettings = useDisciplineStore(s => s.updateSettings);
  const { lock, now } = useRestrainLock(venue);
  const locked = enabled && lock !== null;
  const lockText = lock ? restrainLockLabel(lock, now) : '';
  const duration = formatRestrainMinutes(minutes);

  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<React.ElementRef<'button'>>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const place = useCallback((size: Size) => {
    const btn = buttonRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const next = clampPopover(
      { top: r.top, bottom: r.bottom, left: r.left },
      size,
      { width: window.innerWidth, height: window.innerHeight },
    );
    setPos(p => (p.top === next.top && p.left === next.left ? p : next));
  }, []);

  const measure = useCallback(() => {
    const m = menuRef.current;
    place(m && m.offsetWidth > 0 ? { width: m.offsetWidth, height: m.offsetHeight } : ESTIMATED_SIZE);
  }, [place]);

  const close = useCallback(() => setOpen(false), []);

  // Once rendered, re-place with the real size; keep it on-screen on resize.
  useLayoutEffect(() => {
    if (!open) return;
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [open, measure]);

  useEffect(() => {
    if (!open) return;
    const onOutside = (e: Event): void => {
      const t = e.target as HTMLElement | null;
      if (buttonRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      close();
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        close();
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onOutside);
    document.addEventListener('touchstart', onOutside);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onOutside);
      document.removeEventListener('touchstart', onOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  const toggleMenu = () => {
    if (!open) place(ESTIMATED_SIZE);
    setOpen(v => !v);
  };

  const choose = (m: number) => {
    if (locked) return;
    if (!enabled || m !== minutes) updateSettings({ [keys.enabled]: true, [keys.minutes]: m });
    close();
  };

  const turnOff = () => {
    if (locked) return;
    if (enabled) updateSettings({ [keys.enabled]: false });
    close();
  };

  const status = !enabled ? 'off' : locked ? `${duration} - ${lockText}` : `${duration} - armed`;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={`restrain-toggle ${enabled ? 'on' : ''}${locked ? ' locked' : ''}${className ? ` ${className}` : ''}`}
        onClick={toggleMenu}
        aria-pressed={enabled}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="restrain-toggle"
        data-venue={venue}
        title={`${venue === 'perps' ? 'Perps' : 'Futures'} Restrain ${status}. ${RESTRAIN_EXPLANATION}`}
      >
        {restrainButtonLabel(enabled, minutes)}
      </button>

      {open && createPortal(
        <div
          ref={menuRef}
          className="restrain-menu"
          role="menu"
          aria-label="Restrain duration"
          data-testid="restrain-menu"
          style={{ top: pos.top, left: pos.left }}
        >
          <div className="restrain-menu-title" data-testid="restrain-menu-status">
            {venue === 'perps' ? 'Perps' : 'Futures'} Restrain: {status}
          </div>
          <div className="restrain-menu-grid">
            {RESTRAIN_PRESETS.map(p => {
              const selected = enabled && minutes === p.minutes;
              return (
                <button
                  key={p.minutes}
                  type="button"
                  role="menuitemradio"
                  aria-checked={selected}
                  disabled={locked}
                  className={`restrain-menu-item${p.minutes === 0 ? ' wide' : ''}${selected ? ' selected' : ''}`}
                  data-testid={`restrain-preset-${p.minutes}`}
                  onClick={() => choose(p.minutes)}
                >
                  {p.label}
                </button>
              );
            })}
            <button
              type="button"
              role="menuitemradio"
              aria-checked={!enabled}
              disabled={locked}
              className={`restrain-menu-item wide off${!enabled ? ' selected' : ''}`}
              data-testid="restrain-preset-off"
              onClick={turnOff}
            >
              Off
            </button>
          </div>
          <div className="restrain-menu-footer" data-testid="restrain-menu-footer">
            {locked ? RESTRAIN_MENU_FOOTER_LOCKED : enabled ? RESTRAIN_MENU_FOOTER_ARMED : RESTRAIN_MENU_FOOTER_OFF}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export default RestrainToggle;
